/** A sessão partilhada em que este browser está, por workspace.
 *
 *  Quem partilha fica com a sessão lembrada (o id e a chave), para o
 *  menu Sessão passar a dizer "Gerir partilha da sessão" e para
 *  "Atualizar a sessão" usar a mesma chave. Quem abre uma sessão
 *  partilhada também fica com ela lembrada (para ver com quem está).
 *  Fechar a sessão esquece-a.
 *
 *  E o progresso de abrir uma sessão (descarregar os modelos...), que
 *  um pequeno painel mostra.
 */
import { useSyncExternalStore } from 'react'

export interface SessaoActual {
  partilhaId: string
  /** Só de quem partilhou (é ela que actualiza a sessão no servidor). */
  chave: string | null
  nome: string
  /** Fui eu que a partilhei, neste browser. */
  minha: boolean
}

const CHAVE = 'vn2r.sessao.partilhada'

function criarStore<T>(inicial: T) {
  let atual = inicial
  const ouvintes = new Set<() => void>()
  return {
    ler: () => atual,
    mudar: (v: T) => {
      atual = v
      for (const f of ouvintes) f()
    },
    subscrever: (f: () => void) => {
      ouvintes.add(f)
      return () => {
        ouvintes.delete(f)
      }
    },
  }
}

function lerTodas(): Record<string, SessaoActual> {
  try {
    const raw = localStorage.getItem(CHAVE)
    const o: unknown = raw ? JSON.parse(raw) : {}
    return o && typeof o === 'object' ? (o as Record<string, SessaoActual>) : {}
  } catch {
    return {}
  }
}

const todas = criarStore<Record<string, SessaoActual>>(lerTodas())

function gravarTodas(v: Record<string, SessaoActual>): void {
  todas.mudar(v)
  try {
    localStorage.setItem(CHAVE, JSON.stringify(v))
  } catch {
    /* sem armazenamento: fica só nesta página */
  }
}

export function lerSessaoActual(workspaceId: string | null | undefined): SessaoActual | null {
  if (!workspaceId) return null
  const s = todas.ler()[workspaceId]
  return s && typeof s.partilhaId === 'string' ? s : null
}

export function gravarSessaoActual(workspaceId: string, s: SessaoActual | null): void {
  const v = { ...todas.ler() }
  if (s) v[workspaceId] = s
  else delete v[workspaceId]
  gravarTodas(v)
}

/** Fechar a sessão (ou abrir outra) esquece a partilhada. */
export function esquecerSessaoActual(): void {
  gravarTodas({})
}

export function useSessaoActual(workspaceId: string | null | undefined): SessaoActual | null {
  const v = useSyncExternalStore(todas.subscrever, todas.ler, todas.ler)
  if (!workspaceId) return null
  const s = v[workspaceId]
  return s && typeof s.partilhaId === 'string' ? s : null
}

// -- progresso de abrir uma sessão ------------------------------------------

const progresso = criarStore<string | null>(null)

export function definirProgresso(texto: string | null): void {
  progresso.mudar(texto)
}

export function useProgressoDaSessao(): string | null {
  return useSyncExternalStore(progresso.subscrever, progresso.ler, progresso.ler)
}

// -- pedidos de autorização e lista das sessões ------------------------------

const janelaPedidos = criarStore<boolean>(false)

/** Abre a janela dos pedidos de autorização (as peças minhas que outras
 *  pessoas incluíram nas sessões delas). */
export function abrirPedidosDeSessao(): void {
  janelaPedidos.mudar(true)
}

export function fecharPedidosDeSessao(): void {
  janelaPedidos.mudar(false)
}

export function useJanelaPedidos(): boolean {
  return useSyncExternalStore(janelaPedidos.subscrever, janelaPedidos.ler, janelaPedidos.ler)
}

/** Um número que sobe quando as sessões ou os pedidos mudam (partilhar,
 *  apagar, autorizar), para as listas se lerem outra vez. */
const versao = criarStore<number>(0)

export function sessoesMudaram(): void {
  versao.mudar(versao.ler() + 1)
}

export function useVersaoDasSessoes(): number {
  return useSyncExternalStore(versao.subscrever, versao.ler, versao.ler)
}
